import { Injectable } from '@angular/core';
import { Observable, Subject, timer, of, BehaviorSubject, interval } from 'rxjs';
import { takeUntil, tap, timeout, switchMap, filter, take } from 'rxjs/operators';
import { io, Socket } from 'socket.io-client';
import { Config } from '../config/config';
import { AIChatObservable, StructureObservable, FileUploadObservable } from './observable.service';
import { EventType, ProcessingStatus, WebsocketMessage } from 'src/app/core/models/websocket.model';
import { XSSheet, APISheet } from 'src/app/layout/shell/shell.model';
import { StateObservable } from 'src/app/core/service/observable.service';
import { StructureEvent } from 'src/app/core/models/websocket/structure';
import { AIEvent } from 'src/app/core/models/websocket/aichat';
import { FileUploadEvent } from 'src/app/core/models/websocket/fileupload';
import { HttpHeaders, HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class WebsocketService {

  private socket: Socket | null = null;
  private socketIdSubject = new BehaviorSubject<string | null>(null);
  private isConnecting = false;
  private reconnectionAttempts = 0;
  private maxReconnectionAttempts = 10;
  private baseReconnectionDelay = 1000; // 1 second
  private maxReconnectionDelay = 30000; // 30 seconds
  private persistentReconnectionTimer: any = null;
  private healthCheckInterval: any = null;
  private isDestroyed = false;

  constructor(
    private aiChatObservable: AIChatObservable,
    private structureObservable: StructureObservable,
    private fileUploadObservable: FileUploadObservable,
    private stateObservable: StateObservable,
    private http: HttpClient
    ) {
      this.initializeConnection();
      this.startHealthCheck();
    }

  private propagateAIEvent<T>(type:EventType, data:T) {
      const event:AIEvent = { type: type, data };
      this.aiChatObservable.registerNewUpdate(event);
  }

  private normalizeStructureEvent(raw: any): StructureEvent {
    if (!raw || typeof raw !== 'object') return raw as StructureEvent;

    if (raw.table_id && !raw.tableId) raw.tableId = raw.table_id;
    if (raw.file_id && !raw.fileId) raw.fileId = raw.file_id;

    if (raw.processing_status && !raw.processingStatus) raw.processingStatus = raw.processing_status;
    if (raw.api_status && !raw.apiStatus) raw.apiStatus = raw.api_status;

   
    const hasTopLevelStep = Boolean(raw.step) && !raw.processingStatus;
    if (hasTopLevelStep) {
      raw.processingStatus = {
        step: raw.step,
        step_progress: raw.step_progress ?? raw.stepProgress ?? 0,
        steps_completed: raw.steps_completed ?? raw.stepsCompleted ?? [],
        description: raw.description ?? raw.message ?? '',
      };
    }

    if (raw.processingStatus) {
      if (raw.processingStatus.stepProgress != null && raw.processingStatus.step_progress == null) {
        raw.processingStatus.step_progress = raw.processingStatus.stepProgress;
      }
      if (raw.processingStatus.stepsCompleted && !raw.processingStatus.steps_completed) {
        raw.processingStatus.steps_completed = raw.processingStatus.stepsCompleted;
      }
    }

    if (raw.progress && raw.progress.processed_files && !raw.progress.processedFiles) {
      raw.progress.processedFiles = raw.progress.processed_files;
    }
    if (raw.progress && raw.progress.failed_files && !raw.progress.failedFiles) {
      raw.progress.failedFiles = raw.progress.failed_files;
    }

    return raw as StructureEvent;
  }

  private propagateStructureEvent(data: StructureEvent) {
    const normalized = this.normalizeStructureEvent(data as any);
    this.structureObservable.registerNewUpdate(normalized);
  }

  private propagateFileUploadEvent(data: FileUploadEvent) {
    this.fileUploadObservable.registerNewUpdate(data);
  }

  private createConnection(): Socket {
    return io(Config.websocket_url,{
      reconnectionAttempts: this.maxReconnectionAttempts,
      reconnectionDelay: this.baseReconnectionDelay,
      reconnectionDelayMax: this.maxReconnectionDelay,
      randomizationFactor: 0.5,
      timeout: 10000,
      forceNew: true
    });
  }

  private calculateReconnectionDelay(): number {
    const delay = Math.min(
      this.baseReconnectionDelay * Math.pow(2, this.reconnectionAttempts),
      this.maxReconnectionDelay
    );
    const jitter = delay * 0.25 * (Math.random() * 2 - 1);
    return Math.max(delay + jitter, this.baseReconnectionDelay);
  }

  private startPersistentReconnection(): void {
    if (this.persistentReconnectionTimer || this.isDestroyed) {
      return;
    }

    console.log('Starting persistent reconnection attempts...');
    this.persistentReconnectionTimer = setInterval(() => {
      if (!this.socket?.connected && !this.isConnecting && !this.isDestroyed) {
        console.log(`Persistent reconnection attempt ${this.reconnectionAttempts + 1}/${this.maxReconnectionAttempts}`);
        this.attemptReconnection();
      }
    }, this.calculateReconnectionDelay());
  }

  private stopPersistentReconnection(): void {
    if (this.persistentReconnectionTimer) {
      clearInterval(this.persistentReconnectionTimer);
      this.persistentReconnectionTimer = null;
      console.log('Stopped persistent reconnection attempts');
    }
  }

  private startHealthCheck(): void {
    if (this.healthCheckInterval || this.isDestroyed) {
      return;
    }

    this.healthCheckInterval = setInterval(() => {
      if (!this.isDestroyed && this.socket?.connected) {
        this.socket.emit('ping', Date.now());
      } else if (!this.isDestroyed && !this.socket?.connected && !this.isConnecting) {
        console.log('Health check detected disconnection, attempting reconnection...');
        this.attemptReconnection();
      }
    }, 30000);
  }

  private stopHealthCheck(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  }

  private attemptReconnection(): void {
    if (this.isConnecting || this.isDestroyed || this.reconnectionAttempts >= this.maxReconnectionAttempts) {
      if (this.reconnectionAttempts >= this.maxReconnectionAttempts) {
        console.log('Maximum reconnection attempts reached, starting persistent reconnection...');
        this.startPersistentReconnection();
      }
      return;
    }

    this.reconnectionAttempts++;
    console.log(`Reconnection attempt ${this.reconnectionAttempts}/${this.maxReconnectionAttempts}`);
    this.initializeConnection();
  }

  private resetReconnectionState(): void {
    this.reconnectionAttempts = 0;
    this.stopPersistentReconnection();
  }

  private initializeConnection(): void {
    if (this.isConnecting || (this.socket?.connected && this.socketIdSubject.value) || this.isDestroyed) {
      return;
    }

    this.isConnecting = true;

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
    }

    this.socket = this.createConnection();
    let sheetId: string = '';

    this.socket.on('connect', () => {
      console.log('WebSocket connection established');
      this.resetReconnectionState();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket connection disconnected:', reason);
      this.socketIdSubject.next(null);
      this.propagateAIEvent('progress', null);
      this.isConnecting = false;

      if (reason === 'io server disconnect') {
        console.log('Server initiated disconnect, not attempting automatic reconnection');
      } else {
        setTimeout(() => this.attemptReconnection(), this.calculateReconnectionDelay());
      }
    });

    this.socket.on('error', (error: any) => {
      console.error('WebSocket error:', error);
      this.socketIdSubject.next(null);
      this.isConnecting = false;
      setTimeout(() => this.attemptReconnection(), this.calculateReconnectionDelay());
    });

    this.socket.on('socketId', (socketId: string) => {
      this.socketIdSubject.next(socketId);
      this.isConnecting = false;

      this.sendDummyRequest(socketId);
    });

    this.socket.on('pong', (timestamp: number) => {
      const latency = Date.now() - timestamp;
      console.log(`WebSocket ping latency: ${latency}ms`);
    });

    this.socket.on('progress', (data: ProcessingStatus) => {
      this.propagateAIEvent('progress', data);
    });
    this.socket.on('update', (data: any) => {
      this.propagateAIEvent('update', data);
    });
    this.socket.on('text_stream', (data: string[]) => {
      this.propagateAIEvent('text_stream', data);
    });
    this.socket.on('table_stream', (data: XSSheet | APISheet) => {
      this.propagateAIEvent('table_stream', data);
    });
    this.socket.on('api_table_stream', (data: APISheet) => {
      this.propagateAIEvent('table_stream', data);
    });
    this.socket.on('redirect', (data: string) => {
      sheetId = data;
      this.propagateAIEvent('redirect', data);
    });
    this.socket.on('structure', (data: StructureEvent) => {
      this.propagateStructureEvent(data);
    });
    this.socket.on('fileupload', (data: FileUploadEvent) => {
      this.propagateFileUploadEvent(data);
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      this.socketIdSubject.next(null);
      this.isConnecting = false;
      setTimeout(() => this.attemptReconnection(), this.calculateReconnectionDelay());
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log(`WebSocket reconnected after ${attemptNumber} attempts`);
      this.resetReconnectionState();
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`WebSocket reconnection attempt ${attemptNumber}`);
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('WebSocket reconnection error:', error);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('WebSocket reconnection failed after all attempts');
      this.startPersistentReconnection();
    });

    const timeout$ = timer(10000).pipe(takeUntil(this.socketIdSubject.pipe(filter(id => id !== null))));
    timeout$.subscribe(() => {
      if (this.socketIdSubject.value === null) {
        console.log("WebSocket connection timed out waiting for socketId");
        this.socketIdSubject.next(null);
        this.socket?.disconnect();
        this.isConnecting = false;
        setTimeout(() => this.attemptReconnection(), this.calculateReconnectionDelay());
      }
    });
  }

  connect(): Observable<string | null> {
    return this.socketIdSubject.asObservable().pipe(
      filter(socketId => socketId !== null),
      take(1)
    );
  }

  withWebSocket<T>(requestFn: (headers: HttpHeaders) => Observable<T>): Observable<T> {
    return this.socketIdSubject.pipe(
      filter(socketId => socketId !== null),
      take(1),
      switchMap(socketId => {
        if (!socketId) {
          console.error("WebSocket connection failed or no socketId received.");
          this.stateObservable.setLoading(false);
          return of(null) as Observable<any>;
        }

        const headers = new HttpHeaders().set('x-socket-id', socketId);

        return requestFn(headers).pipe(
          tap(() => {
            this.stateObservable.setLoading(false);
          })
        );
      })
    );
  }

  // Method to manually reconnect if needed
  reconnect(): void {
    console.log('Manual reconnection requested');
    this.resetReconnectionState();
    if (this.socket) {
      this.socket.disconnect();
    }
    this.socketIdSubject.next(null);
    this.isConnecting = false;
    this.initializeConnection();
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  getSocketId(): string | null {
    return this.socketIdSubject.value;
  }

  getConnectionStats(): {
    isConnected: boolean;
    reconnectionAttempts: number;
    maxAttempts: number;
    hasPersistentReconnection: boolean;
  } {
    return {
      isConnected: this.isConnected(),
      reconnectionAttempts: this.reconnectionAttempts,
      maxAttempts: this.maxReconnectionAttempts,
      hasPersistentReconnection: !!this.persistentReconnectionTimer
    };
  }

  destroy(): void {
    this.isDestroyed = true;
    this.stopPersistentReconnection();
    this.stopHealthCheck();

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.socketIdSubject.complete();
  }

  private sendDummyRequest(socketId: string): void {
    const headers = new HttpHeaders().set('x-socket-id', socketId);
    const dummyUrl = `${Config.backend_url}/users/info`;

    this.http.get(dummyUrl, { headers }).subscribe({
      next: (response) => {
        console.log('Dummy request sent successfully with socket ID:', socketId);
      },
      error: (error) => {
        console.log('Dummy request failed, but websocket connection is established:', error);
      }
    });
  }

}
